import { AlertCircle, CheckCircle, Info, X, XCircle } from "lucide-react";
import React from "react";

export interface CustomAlertProps {
  type: "success" | "error" | "warning" | "info";
  title?: string;
  message: string;
  isVisible: boolean;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

/**
 * CustomAlert component displays alert messages to users.
 * It can be used to show notifications, warnings, or errors in a customizable format.
 */
const CustomAlert: React.FC<CustomAlertProps> = ({
  type,
  title,
  message,
  isVisible,
  onClose,
  autoClose = true,
  autoCloseDelay = 5000,
}) => {
  React.useEffect(() => {
    if (isVisible && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, autoClose, autoCloseDelay, onClose]);

  if (!isVisible) return null;

  const getAlertStyles = () => {
    switch (type) {
      case "success":
        return {
          container: "bg-green-50 border-green-200 text-green-800",
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
        };
      case "error":
        return {
          container: "bg-red-50 border-red-200 text-red-800",
          icon: <XCircle className="w-5 h-5 text-red-500" />,
        };
      case "warning":
        return {
          container: "bg-yellow-50 border-yellow-200 text-yellow-800",
          icon: <AlertCircle className="w-5 h-5 text-yellow-500" />,
        };
      case "info":
        return {
          container: "bg-blue-50 border-blue-200 text-blue-800",
          icon: <Info className="w-5 h-5 text-blue-500" />,
        };
      default:
        return {
          container: "bg-gray-50 border-gray-200 text-gray-800",
          icon: <Info className="w-5 h-5 text-gray-500" />,
        };
    }
  };

  const { container, icon } = getAlertStyles();

  return (
    <div className="fixed top-4 right-4 z-[1000000] max-w-md w-full">
      <div className={`border rounded-lg p-4 shadow-lg ${container}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">{icon}</div>
          <div className="ml-3 flex-1">
            {title && <h3 className="text-sm font-medium mb-1">{title}</h3>}
            <p className="text-sm whitespace-pre-wrap">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onClose}
              className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomAlert;
